import { Messages } from '../../codegen/Localize';
import { IActionContext } from '../helpers/ActionContext';
import {
  FlowErrorCode,
  ProjectErrorCode,
  SchedulingErrorCode,
  ViewErrorCode,
  WorkbookErrorCode,
} from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { Flows } from '../resources/Flows';
import { Projects } from '../resources/Projects';
import { Schedules } from '../resources/Schedules';
import { Views } from '../resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ToasterService, ToastType } from './ToasterService';

export interface IRenameDialogScope extends ng.IScope {
  description: string;
  input: {
    text: string;
    maxLength?: number;
  };
}

interface RenameActionParams {
  item: INamedItemInfo;
  actionContext: IActionContext;
  dialogTitle: string;
  dialogDescription: string;
  renameFn: (id: string, name: string) => ng.IPromise<IResult>;
  getSuccessMessage: (newName: string) => string;
  defaultErrorMessage: string;
}

export class RenameAction {

  public static $inject = [
    'ConfirmActionDialog',
    'Flows',
    'Projects',
    'Schedules',
    'ToasterService',
    'Views',
    'Workbooks',
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private Flows: Flows,
    private Projects: Projects,
    private Schedules: Schedules,
    private toaster: ToasterService,
    private Views: Views,
    private Workbooks: Workbooks) {}

  public renameProject(project: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: project,
      actionContext: actionContext,
      dialogTitle: Messages.renameProject(),
      dialogDescription: Messages.confirmRenameProject({ PROJECT_NAME: L10n.elideIfLong(project.name) }),
      renameFn: (id: string, name: string) => this.Projects.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.projectRenamed({ PROJECT_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.projectRenameError({ PROJECT_NAME: L10n.elideIfLong(project.name) })
    });
  }

  public renameWorkbook(workbook: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: workbook,
      actionContext: actionContext,
      dialogTitle: Messages.renameWorkbook(),
      dialogDescription: Messages.confirmRenameWorkbook(),
      renameFn: (id: string, name: string) => this.Workbooks.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.workbookRenamed({ WORKBOOK_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.workbookRenameError({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) })
    });
  }

  public renameView(view: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: view,
      actionContext: actionContext,
      dialogTitle: Messages.renameView(),
      dialogDescription: Messages.confirmRenameView(),
      renameFn: (id: string, name: string) => this.Views.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.viewRenamed({ VIEW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.viewRenameError({ VIEW_NAME: L10n.elideIfLong(view.name) })
    });
  }

  public renameSchedule(schedule: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: schedule,
      actionContext: actionContext,
      dialogTitle: Messages.renameSchedule(),
      dialogDescription: Messages.confirmRenameSchedule({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) }),
      renameFn: (id: string, name: string) => this.Schedules.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.updateScheduleNameSuccess({ ITEM_NAME: schedule.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) })
    });
  }

  public renameFlow(flow: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: flow,
      actionContext: actionContext,
      dialogTitle: Messages.renameFlow(),
      dialogDescription: Messages.confirmRenameFlow({ FLOW_NAME: L10n.elideIfLong(flow.name) }),
      renameFn: (id: string, name: string) => this.Flows.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.flowRenamed({ FLOW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.flowRenameError({ FLOW_NAME: L10n.elideIfLong(flow.name) })
    });
  }

  private rename(params: RenameActionParams): ng.IPromise<IResult> {
    const { item, actionContext, dialogTitle, dialogDescription, renameFn, getSuccessMessage, defaultErrorMessage } = params;

    const scope = <IRenameDialogScope> actionContext.scope.$new();
    scope.description = dialogDescription;
    scope.input = { text: item.name, maxLength: 255 };

    return this.ConfirmActionDialog.open({
      scope: scope,
      title: dialogTitle,
      bodyTemplateUrl: 'confirmActionDialogInput.html',
      actionName: Messages.rename(),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        const newName = scope.input.text;
        return renameFn(item.id, newName)
          .then((result: IResult) => {
            if (result.errors && result.errors.length > 0) {
              const errorMessage = this.getErrorMessage(result.errors[0], item, defaultErrorMessage);
              dialogInstance.showErrorMessage(errorMessage);
            } else {
              const successMessage = getSuccessMessage(newName);
              this.toaster.pop({ type: ToastType.Info, text: successMessage });
            }
          });
      }
    }).result.finally(() => scope.$destroy());
  }

  private getErrorMessage(error: IError, item: INamedItemInfo, defaultErrorMessage: string) {
    switch (error.code) {
      case FlowErrorCode.FlowNameAlreadyExists:
        return Messages.errorFlowNameAlreadyExists();
      case ProjectErrorCode.ProjectNameAlreadyExists:
        return Messages.errorProjectNameAlreadyExists();
      case SchedulingErrorCode.SchedulingNameAlreadyExists:
        return Messages.errorScheduleNameAlreadyExists();
      case ViewErrorCode.ViewNameAlreadyExists:
        return Messages.errorViewNameAlreadyExists();
      case WorkbookErrorCode.WorkbookNameAlreadyExists:
        return Messages.errorWorkbookNameAlreadyExists();
      default:
        return defaultErrorMessage;
    }
  }
}

Module.instance().service('RenameAction', RenameAction);
